import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCcw, ShieldCheck, PlayCircle, RotateCw, AlertTriangle, Copy, Check, ExternalLink,
} from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DomainStatusBadge, SslStatusBadge, ProviderBadge } from "./DomainBadges";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; }
}

function Row({ label, value, mono = false, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-white/5 last:border-0" data-testid={testid}>
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right truncate min-w-0 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}

function DnsRecordsTable({ records }) {
  if (!records?.length) return <div className="text-xs text-white/40 italic">Belum ada record.</div>;
  const copy = (r) => {
    const txt = `${r.record_type}\t${r.name}\t${r.value}\tTTL ${r.ttl}`;
    navigator.clipboard.writeText(txt);
    toast.success("Disalin ke clipboard.");
  };
  return (
    <table className="w-full text-xs" data-testid="dom-dns-records">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10">
          <th className="py-2 font-bold">Type</th>
          <th className="py-2 font-bold">Name</th>
          <th className="py-2 font-bold">Value</th>
          <th className="py-2 font-bold">TTL</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <tr key={i} className="border-b border-white/5" data-testid={`dom-record-${i}`}>
            <td className="py-2 font-mono text-cyan-300">{r.record_type}</td>
            <td className="py-2 font-mono text-white">{r.name}</td>
            <td className="py-2 font-mono text-white/80 truncate max-w-[220px]">{r.value}</td>
            <td className="py-2 text-white/60">{r.ttl}</td>
            <td className="py-2 text-right">
              <Button size="sm" variant="ghost" onClick={() => copy(r)} className="text-white/60 hover:bg-white/10">
                <Copy size={12} />
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DomainDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dr, pr] = await Promise.all([
        api.get(`/platform/domains/${id}`),
        api.get("/platform/domains/providers"),
      ]);
      setD(dr.data);
      setProviders(pr.data);
    } catch (e) {
      if (e?.response?.status === 404) { setD(null); }
      else notifyError(e);
    }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const act = async (verb, body = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/platform/domains/${id}/${verb}`, body);
      toast.success(`Aksi ${verb} berhasil.`);
      load();
      return r.data;
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusy(false); }
  };

  if (loading) {
    return <PlatformLayout title="Domain Detail">
      <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
    </PlatformLayout>;
  }
  if (!d) {
    return <PlatformLayout title="Domain Detail">
      <div className="p-6 rounded-2xl border border-white/10 text-center" data-testid="dom-not-found">
        <AlertTriangle size={24} className="mx-auto text-rose-300 mb-2" />
        <div className="text-white/70">Domain tidak ditemukan.</div>
        <Button onClick={() => nav("/platform/domains")} variant="ghost" className="mt-4 text-white/60 hover:text-white hover:bg-white/10">← Kembali</Button>
      </div>
    </PlatformLayout>;
  }

  return (
    <PlatformLayout title={d.fqdn}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/domains")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dom-back-btn">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="dom-refresh-btn">
            <RefreshCcw size={14} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => act("apply-dns")} disabled={busy} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="dom-apply-dns">
            <PlayCircle size={14} className="mr-1" /> Apply DNS
          </Button>
          <Button onClick={() => act("verify-dns", {})} disabled={busy} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="dom-verify-dns">
            <Check size={14} className="mr-1" /> Verify DNS
          </Button>
          <Button onClick={() => act("issue-ssl", { force: false })} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="dom-issue-ssl">
            <ShieldCheck size={14} className="mr-1" /> Issue SSL
          </Button>
          <Button onClick={() => act("renew-ssl")} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="dom-renew-ssl">
            <RotateCw size={14} className="mr-1" /> Renew SSL
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-white font-display font-black text-xl" data-testid="dom-detail-fqdn">{d.fqdn}</div>
        <DomainStatusBadge status={d.status} />
        <SslStatusBadge status={d.ssl_status} />
        <ProviderBadge code={d.dns_provider} providers={providers?.dns} kind="dns" />
        <ProviderBadge code={d.ssl_provider} providers={providers?.ssl} kind="ssl" />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="dom-section-records">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">Expected DNS Records</div>
            <DnsRecordsTable records={d.dns_records} />
            <div className="text-[10px] text-white/40 mt-3 italic">
              {d.dns_provider === "MANUAL"
                ? "Manual mode: tambahkan record ini di panel DNS provider Anda, lalu klik Verify DNS."
                : `Provider ${d.dns_provider}: Apply DNS akan mengirim payload ke API provider (dry-run jika kredensial belum di-set).`}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="dom-section-ssl-history">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-3">SSL Last Issue</div>
            {d.ssl_last_issue ? (
              <pre className="text-[10px] font-mono text-white/70 bg-black/30 p-3 rounded overflow-auto max-h-64">{JSON.stringify(d.ssl_last_issue, null, 2)}</pre>
            ) : <div className="text-xs text-white/40 italic">Belum ada issue SSL.</div>}
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="dom-section-info">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">Info</div>
            <Row label="FQDN" value={d.fqdn} mono testid="dom-info-fqdn" />
            <Row label="Domain" value={d.domain} mono />
            <Row label="Subdomain" value={d.subdomain || "—"} />
            <Row label="Target IP" value={d.target_ip} mono />
            <Row label="Target Host" value={d.target_hostname} mono />
            <Row label="Created" value={fmt(d.created_at)} />
            <Row label="Updated" value={fmt(d.updated_at)} />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="dom-section-dns-config">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">DNS Provider</div>
            <Row label="Provider" value={d.dns_provider} />
            {Object.entries(d.dns_config || {}).map(([k, v]) => <Row key={k} label={k} value={String(v)} mono />)}
            <Row label="Last Apply" value={fmt(d.dns_last_apply?.at)} />
            <Row label="Last Verify" value={fmt(d.dns_last_verify?.at)} />
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="dom-section-ssl-config">
            <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">SSL Provider</div>
            <Row label="Provider" value={d.ssl_provider} />
            <Row label="Status" value={d.ssl_status} />
            <Row label="Expires" value={fmt(d.ssl_expires_at)} />
            {Object.entries(d.ssl_config || {}).map(([k, v]) => <Row key={k} label={k} value={String(v)} mono />)}
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Link to="/platform/domains" className="text-xs text-white/50 hover:text-white underline">← Semua domain</Link>
      </div>
    </PlatformLayout>
  );
}
